// =====================================================================================
// INVRT Radar — web console — display formatting
//
// Credits arrive as a Postgres `numeric` — a number OR a decimal string. Everything here
// FORMATS; nothing sums, multiplies or rounds a money figure for display as if it were
// authoritative. Every deterministic figure the console shows (a quote's credits, a
// balance, a window total) is a figure Postgres computed and the API handed over.
// =====================================================================================

import type { Numeric } from "./types";

/**
 * Renders a credit figure exactly as received, trimmed of pointless trailing zeros.
 * 0.5 stays 0.5; 2 stays 2; "0.250" becomes 0.25. Never rounds a value away.
 */
export function formatCredits(value: Numeric | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const text = typeof value === "number" ? String(value) : value.trim();
  if (text === "") return "—";
  if (!/^-?\d+(\.\d+)?$/.test(text)) return text;

  const [whole = "0", fraction] = text.split(".");
  const trimmed = fraction?.replace(/0+$/, "") ?? "";
  const sign = whole.startsWith("-") ? "-" : "";
  const digits = sign ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return trimmed === "" ? `${sign}${grouped}` : `${sign}${grouped}.${trimmed}`;
}

/** "2 credits" / "1 credit" / "0.5 credits". */
export function formatCreditsWithUnit(value: Numeric | null | undefined): string {
  const rendered = formatCredits(value);
  if (rendered === "—") return "—";
  return rendered === "1" ? "1 credit" : `${rendered} credits`;
}

const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const DATE_ONLY = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** UK format: 12 Aug 2026, 14:05. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return DATE_TIME.format(parsed);
}

/** UK format: 12 August 2026. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return DATE_ONLY.format(parsed);
}

/** "in 12 minutes" / "3 minutes ago" / "now" — for quote expiry and event feeds. */
export function formatRelative(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "—";
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return iso;

  const deltaSeconds = Math.round((target.getTime() - now.getTime()) / 1000);
  const absolute = Math.abs(deltaSeconds);
  if (absolute < 45) return "now";

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["minute", 60],
    ["hour", 3600],
    ["day", 86_400],
    ["month", 2_592_000],
    ["year", 31_536_000],
  ];

  let unit: Intl.RelativeTimeFormatUnit = "minute";
  let divisor = 60;
  for (const [candidateUnit, candidateDivisor] of units) {
    if (absolute >= candidateDivisor) {
      unit = candidateUnit;
      divisor = candidateDivisor;
    }
  }

  return new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" }).format(
    Math.round(deltaSeconds / divisor),
    unit,
  );
}

/** ISO 4217 codes Radar actually sells packs in today, lower-cased for lookup either way —
 * `credit_packs.currency` defaults to lower-case, the checkout response returns upper-case. */
const MINOR_CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  gbp: "£",
  eur: "€",
};

/**
 * Formats a minor-unit integer (e.g. USD cents) as a currency string without any
 * floating-point arithmetic on the amount: the whole-unit part comes from integer division,
 * the minor-unit part from modulo, and the remainder is padded to two digits. `price_minor`
 * and `amount_minor` arrive from the server as exact integers for precisely this reason —
 * this only ever formats a figure Postgres or Stripe already computed, never derives one.
 */
export function formatMinorCurrency(amountMinor: number, currency: string): string {
  const symbol = MINOR_CURRENCY_SYMBOLS[currency.toLowerCase()];
  const prefix = symbol ?? `${currency.toUpperCase()} `;
  const negative = amountMinor < 0;
  const absolute = Math.trunc(Math.abs(amountMinor));
  const whole = Math.trunc(absolute / 100);
  const minor = String(absolute % 100).padStart(2, "0");
  return `${negative ? "-" : ""}${prefix}${whole.toLocaleString("en-GB")}.${minor}`;
}

/**
 * A per-credit comparison figure for the packs list — informational only, and always
 * prefixed with "≈" so it can never be mistaken for a committed price. The committed price
 * is the pack's own amount, formatted verbatim by formatMinorCurrency above; this is a
 * rounded aid for comparing packs against each other, never a figure Radar charges, stores
 * or quotes.
 */
export function formatEffectiveRate(
  amountMinor: number,
  credits: Numeric,
  currency: string,
): string {
  const creditsNumber = typeof credits === "number" ? credits : Number.parseFloat(credits);
  if (!Number.isFinite(creditsNumber) || creditsNumber <= 0) return "—";
  const perCredit = amountMinor / 100 / creditsNumber;
  const symbol = MINOR_CURRENCY_SYMBOLS[currency.toLowerCase()] ?? `${currency.toUpperCase()} `;
  return `≈ ${symbol}${perCredit.toFixed(4)} per credit`;
}

/** Turns "reveal_contact" into "Reveal contact" for a table cell or a chip. */
export function humaniseAction(action: string | null | undefined): string {
  if (!action) return "—";
  const spaced = action.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Plural helper that keeps copy honest: "1 contact", "3 contacts", "no contacts". */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  if (count === 0) return `no ${plural}`;
  return `${count.toLocaleString("en-GB")} ${count === 1 ? singular : plural}`;
}

/** Reads a string leaf out of an untyped provider payload without pretending it is typed. */
export function readString(
  source: Record<string, unknown> | null | undefined,
  ...keys: string[]
): string | undefined {
  if (!source) return undefined;
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return undefined;
}

/**
 * A person's display name from a stored payload: full_name / name when present, otherwise
 * first_name + last_name joined (the shape the identification pipeline actually stores).
 * Undefined when there is nothing to show, so callers choose their own honest fallback.
 */
export function personDisplayName(
  person: Record<string, unknown> | null | undefined,
): string | undefined {
  const full = readString(person, "full_name", "name");
  if (full) return full;
  const parts = [readString(person, "first_name"), readString(person, "last_name")].filter(
    (part): part is string => typeof part === "string",
  );
  return parts.length > 0 ? parts.join(" ") : undefined;
}
